import {
  InjectableCanvasTopologyProvider,
  type CanvasTopologyScope,
  type CanvasTopologySource,
} from '@de/ce-editor/topology/canvas-topology-provider';
import { FORBIDDEN_KEYS } from '@de/utils/interpolation/forbiddenKeys';

/**
 * One referenceable block, reduced to what a declaration may read.
 *
 * `dataSourceNames` are opaque registry keys copied from the host contract.
 * They are not resolved, and no other host field is copied.
 */
export interface TopologySiblingProjection {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly dataSourceNames: readonly string[];
}

export interface TopologyProjection {
  readonly siblings: Readonly<Record<string, TopologySiblingProjection>>;
}

/**
 * Project the host's canvas topology into the interpolation bag.
 *
 * The host contract is listed on every call so live source names stay
 * current. Only identity fields are copied, so a host that attaches extra
 * fields to a node cannot leak them into declaration templates.
 *
 * Ids in {@link FORBIDDEN_KEYS} are left out: interpolation never reads them,
 * so a reference to one is reported as an absent block instead of fetching
 * with an unresolved value.
 */
export function projectCanvasTopology(
  source: CanvasTopologySource,
  scope: CanvasTopologyScope = {},
): TopologyProjection {
  const siblings: Record<string, TopologySiblingProjection> = {};
  for (const node of new InjectableCanvasTopologyProvider(source).list(scope)) {
    if (FORBIDDEN_KEYS.has(node.id)) continue;
    siblings[node.id] = {
      id: node.id,
      label: node.label,
      kind: node.kind,
      dataSourceNames: [...node.dataSourceNames],
    };
  }
  return { siblings };
}
